import React from "react";
import { Helmet } from "react-helmet-async";

// rushmyfood.com redirects here, so this subdomain is the canonical origin for
// every public URL. Canonicals must point at it or the redirect splits equity.
export const SITE_URL = "https://app.rushmyfood.com";
export const SITE_NAME = "RushMyFood";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

/**
 * Per-route document head. Without this every route inherited the single
 * static <title> and the create-react-app placeholder description from
 * public/index.html, so Google discarded our description and wrote its own
 * snippet from body text.
 *
 * @param title       page title, minus the site-name suffix (omit on Home)
 * @param description 150-160 chars; longer gets truncated in the SERP
 * @param path        route path ("/pickup") used to build the canonical URL
 * @param image       absolute or root-relative OG image
 * @param noindex     keep transactional/account pages out of the index
 * @param schema      JSON-LD object, or array of them
 */
export default function Seo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  noindex = false,
  schema,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Order Food Online`;
  const canonical = path ? `${SITE_URL}${path}` : SITE_URL;
  const ogImage = image?.startsWith("http") ? image : `${SITE_URL}${image}`;
  const blocks = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <link rel="canonical" href={canonical} />
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex,follow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
